<script>
    import { createEventDispatcher } from 'svelte';
    import pb from '../lib/pocketbase';
    import { toasts } from 'svelte-toasts';
    import Swal from 'sweetalert2';

    export let newEntity;
    export let isObjectPaneOpen;
    export let toggleObjectPane;

    let objetos = [];
    let roleObjetos = [];
    let checkedItems = new Set();

    let activeTab = 'financiero';
    let tree = [];

    const dispatch = createEventDispatcher();

    async function fetchObjetos() {
        try {
            const resObjetos = await pb.collection('objeto').getFullList({
                sort: 'parent_id',
                $autoCancel: false
            });

            objetos = resObjetos.map(o => ({
                id: o.id,
                _id: o._id,
                parent_id: o.parent_id || null,
                descrip: o.descrip,
                icon: o.modulo_icon,
            }));

            const resRoleObjetos = await pb.collection('role_objeto').getFullList({
                expand: 'objeto',
                filter: `role.id = "${newEntity.id}"`,
                $autoCancel: false
            });

            roleObjetos = resRoleObjetos.map(ro => {
                const obj = ro.expand.objeto;
                return {
                    ...obj,
                    CRUD: ro.CRUD || '',
                    checkConsultar: ro.CRUD.includes('C'),
                    checkAgregar: ro.CRUD.includes('A'),
                    checkModificar: ro.CRUD.includes('M'),
                    checkEliminar: ro.CRUD.includes('E'),
                };
            });

            // Limpiar y marcar seleccionados
            checkedItems.clear();
            roleObjetos.forEach(obj => {
                checkedItems.add(obj.id); // Agrega el id a checkedItems
                obj.checked = true; // Para que el checkbox de la lista principal se marque
            });
            
            tree = construirArbol();
        } catch (error) {
            console.error('Error al cargar objetos:', error);
        }
    }

    function marcarSeleccionados() {
        roleObjetos.forEach(obj => checkedItems.add(obj.id));
    }

    function toggleCheck(id) {
        if (checkedItems.has(id)) {
            checkedItems.delete(id);
        } else {
            checkedItems.add(id);
        }
    }
    async function saveParentObjects() {
        
        // Obtener todos los objetos desde PocketBase
        const allObjects = await pb.collection('objeto').getFullList();

        // Filtrar los objetos que están en checkedItems y tienen parent_id vacío o null
        const parentObjects = allObjects.filter(node => 
            node.parent_id === '' || node.parent_id === null
        );
        
        // Guardar los objetos filtrados en role_objeto
        for (const node of parentObjects) {
            await pb.collection('role_objeto').create({
                role: newEntity.id,
                objeto: node.id,
                CRUD: 'ROOT'
            });
        }
    }

    async function handleSave() {
        try {
            // Primero, guardar objetos con parent_id null o vacío
            await saveParentObjects();

            // Borrar objetos existentes que no tengan parent_id vacío o null
            const existingRoleObjetos = await pb.collection('role_objeto').getFullList({
                filter: `role = "${newEntity.id}"`,
                $autoCancel: false
            });

            // Filtrar los objetos que no deben eliminarse
            const objectsToDelete = existingRoleObjetos.filter(roleObjeto => {
                // Suponiendo que roleObjeto tiene un campo 'objeto' que hace referencia al objeto
                const node = findNodeById(tree, roleObjeto.objeto); // Encuentra el nodo por ID
                return node && (node.parent_id !== null && node.parent_id !== '');
            });

            // Eliminar solo los objetos filtrados
            for (const roleObjeto of objectsToDelete) {
                await pb.collection('role_objeto').delete(roleObjeto.id);
            }

            // Guardar el resto de los objetos
            for (const itemId of checkedItems) {
                const crudString = createCRUDString(itemId);
                await pb.collection('role_objeto').create({
                    role: newEntity.id,
                    objeto: itemId,
                    CRUD: crudString
                });
            }

            dispatch('saved'); // Disparar evento después de guardar
            toggleObjectPane(); // Cerrar el panel

            toasts.add({
                title: 'Éxito',
                description: 'Objetos guardados exitosamente',
                duration: 3000, // Duración de 3 segundos
                placement: 'bottom-right',
                type: 'success',
                theme: 'dark',
                showProgress: true
            });
        } catch (error) {
            Swal.fire('Error', 'No se pudieron guardar los objetos', 'error');
            console.error('Error al guardar:', error);
        }
    }

    function createCRUDString(itemId) {
        let crudString = '';

        // Encontrar el nodo correspondiente al id
        const node = findNodeById(tree, itemId);
        if (node) {
            // Concatenar 'C', 'A', 'M', 'E' según el estado de los checkboxes
            crudString += node.checkConsultar ? 'C' : '';
            crudString += node.checkAgregar ? 'A' : '';
            crudString += node.checkModificar ? 'M' : '';
            crudString += node.checkEliminar ? 'E' : '';
        }

        return crudString || 'C';
    }

    function findNodeById(nodes, id) {
        for (const node of nodes) {
            if (node.id === id) return node;
            const found = findNodeById(node.children || [], id);
            if (found) return found;
        }
        return null;
    }

    function construirArbol(parentId = null) {
        return objetos
            .filter(obj => obj.parent_id === parentId)
            .map(obj => ({
                ...obj,
                isExpanded: true,
                checkConsultar: roleObjetos.find(ro => ro.id === obj.id)?.checkConsultar || false, // Se marca si está incluido en roleObjetos
                checkAgregar: roleObjetos.find(ro => ro.id === obj.id)?.checkAgregar || false,
                checkModificar: roleObjetos.find(ro => ro.id === obj.id)?.checkModificar || false,
                checkEliminar: roleObjetos.find(ro => ro.id === obj.id)?.checkEliminar || false,
                children: construirArbol(obj._id)
            }));
    }

    function toggleExpand(nodo) {
        nodo.isExpanded = !nodo.isExpanded;
        tree = [...tree]; // Forzar actualización
    }

    $: if (isObjectPaneOpen && newEntity?.id) {
        fetchObjetos();
    }


    function checkAll() {
        tree = [...tree]; // Forzar actualización
        objetos.forEach(obj => {
            checkedItems.add(obj.id);
            obj.checked = true; // Marcar el objeto como seleccionado
        });
        tree = [...tree]; // Forzar actualización
    }

    function uncheckAll() {
        tree = [...tree]; // Forzar actualización
        checkedItems.clear(); // Limpiar todos los elementos seleccionados
        objetos.forEach(obj => {
            obj.checked = false; // Desmarcar todos los objetos
        });
        tree = [...tree]; // Forzar actualización
    }

    function invertCheck() {
        tree = [...tree]; // Forzar actualización
        objetos.forEach(obj => {
            if (checkedItems.has(obj.id)) {
                checkedItems.delete(obj.id);
                obj.checked = false; // Desmarcar el objeto
            } else {
                checkedItems.add(obj.id);
                obj.checked = true; // Marcar el objeto
            }
        });
        tree = [...tree]; // Forzar actualización
    }
</script>

<!-- Vista del panel -->
{#if isObjectPaneOpen}
    <div class="pane open flex flex-col h-full">
        <div class="p-4 bg-white shadow-xl rounded-l-lg h-full overflow-y-auto">
            <button
                class="absolute top-3 right-3 text-gray-600 hover:text-red-600 transition"
                on:click={toggleObjectPane}
            >
                <i class="fas fa-times"></i>
            </button>
            <br>

            <h2 class="text-xl font-bold">Establecer objetos</h2>
            <!-- Botones para Check All, Uncheck All, Invert Check -->
            <div class="flex justify-end space-x-4 mb-4">
                <button on:click={checkAll} class="px-4 py-2 bg-blueGray-600 text-white rounded hover:bg-blue-600">
                    Marcar todos
                </button>
                <button on:click={uncheckAll} class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">
                    Desmarcar todos
                </button>
                <button on:click={invertCheck} class="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600">
                    Invertir marcas
                </button>
            </div>
            <div class="flex justify-end space-x-4 mb-4">
                <button
                    on:click={() => (activeTab = 'financiero')}
                    class="px-4 py-2 border-b-2"
                    class:font-bold={activeTab === 'financiero'}
                >
                    Financiero
                </button>
                <button
                    on:click={() => (activeTab = 'contable')}
                    class="px-4 py-2 border-b-2"
                    class:font-bold={activeTab === 'contable'}
                >
                    Contable
                </button>
            </div>

            <div class="grid grid-cols-1 gap-6 max-h-60 overflow-y-auto">
                {#if activeTab === 'financiero'}
                    <ul>
                        {#each tree as nodo (nodo._id)}
                            <li>
                                <i class={`fas ${nodo.icon} mr-2`}></i>
                                {nodo.descrip}
                                {#if nodo.children.length > 0}
                                    <span 
                                        on:click={() => toggleExpand(nodo)} 
                                        class="cursor-pointer ml-2"
                                    >
                                        <i class={`fas ${nodo.isExpanded ? 'fa-minus' : 'fa-plus'}`}></i>
                                    </span>
                                {/if}
                                {#if nodo.isExpanded}
                                    <ul class="pl-4">
                                        {#each nodo.children as child (child._id)}
                                            <li>
                                                <div class="flex space-x-4 mt-2 pl-4">
                                                    <!-- Checkbox principal -->
                                                    <label class="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={checkedItems.has(child.id) || child.checked}
                                                            on:change={() => toggleCheck(child.id)}
                                                            tree = [...tree]
                                                        />
                                                        <i class={`fas ${child.icon} mr-2`}></i>
                                                        <span>{child.descrip}</span>
                                                    </label>

                                                    <!-- Contenedor para las opciones CAME -->
                                                    <div class="flex-grow flex justify-end">
                                                        {#each [
                                                            { binding: 'checkConsultar', icon: 'fa-eye', title: 'Consultar' },
                                                            { binding: 'checkAgregar', icon: 'fa-plus', title: 'Agregar' },
                                                            { binding: 'checkModificar', icon: 'fa-edit', title: 'Modificar' },
                                                            { binding: 'checkEliminar', icon: 'fa-trash-alt', title: 'Eliminar' }
                                                        ] as action}
                                                            <label class="flex items-center space-x-1">
                                                                <input type="checkbox" bind:checked={child[action.binding]} />
                                                                <i class={`fas ${action.icon} mr-2`} title={action.title}></i>
                                                            </label>
                                                        {/each}
                                                    </div>
                                                </div>
                                                <hr class="border-t border-gray-200 my-2" />
                                            </li>
                                        {/each}
                                    </ul>
                                {/if}
                            </li>
                        {/each}
                    </ul>
                {/if}
                {#if activeTab === 'contable'}
                    <h1>Contable</h1>
                {/if}
            </div>

            <button on:click={handleSave} class="mt-4 px-4 py-2 bg-blueGray-600 text-white rounded hover:bg-indigo-600">
                {newEntity ? 'Actualizar' : 'Crear'}
            </button>
            <button on:click={toggleObjectPane} class="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">Cerrar</button>
        </div>
    </div>
{/if}